"use client"

/**
 * IntercomButton - Trigger Intercom chat on click
 *
 * Can be used as a button wrapper or standalone.
 * Works both with and without IntercomProvider context.
 * Falls back to standalone openIntercomChat when outside provider.
 */

import { useCallback, type ReactNode, type MouseEvent } from "react"
import { useIntercom, openIntercomChat } from "./IntercomProvider"

interface IntercomButtonProps {
    children: ReactNode
    className?: string
    message?: string // Pre-populate chat with this message
    disabled?: boolean
    onClick?: (e: MouseEvent<HTMLButtonElement>) => void // Additional onClick handler
}

export default function IntercomButton({
    children,
    className,
    message,
    disabled,
    onClick,
}: IntercomButtonProps) {
    const { isLoading } = useIntercom()

    const handleClick = useCallback(
        (e: MouseEvent<HTMLButtonElement>) => {
            e.preventDefault()
            onClick?.(e)
            openIntercomChat(message)
        },
        [message, onClick]
    )

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={disabled || isLoading}
            className={className || ""}
        >
            {children}
        </button>
    )
}
